import React from 'react';
import NextLink from 'next/link';
import getConfig from 'next/config';
import Head from 'next/head';
import remark from 'remark';
import remark2react from 'remark-react';
import Container from '@lendi-ui/container';
import { Link } from '@lendi-ui/typography';
import BackLink from '../../components/BackLink';
import { BottomCta, InlineCtaHOC } from '../../components/ArticleCta';
import ArticleTags from '../../components/ArticleTags';
import ReadMoreArticles from '../../components/ReadMoreArticles';
import RelatedArticles from '../../components/RelatedArticles';
import ArticlePrimaryVideo from '../../components/ArticlePrimaryVideo';
import Drawer from '../../components/Drawer';
import {
  ArticleBody,
  ArticleCategoryLink,
  ArticleCategory,
  ArticleInfo,
  ArticlePrimaryImage,
  ArticleTitle,
  ArticleWrapper,
  AuthorInfo,
  AuthorName,
  ReadTimeInfo,
  Ul,
  Table,
} from '../../components/Article/index.style';
import { Article, Category } from '../../utils/schema';
import {
  articleUrl,
  loadArticle,
  loadArticles,
  loadReadMoreArticles,
  loadCategories,
  eventTracking,
} from '../../utils';
import { createClient, Entry } from 'contentful';

const NUMBER_OF_RELATED_ARTICLES = 4;

interface ArticlePageProps {
  article: Article;
  categories: Category[];
  category: Category;
}

interface ArticlePageState {
  relatedArticles: Article[];
  readMoreArticles: Article[];
  showDrawer: boolean;
}

class ArticlePage extends React.Component<ArticlePageProps, ArticlePageState> {
  state: ArticlePageState = {
    relatedArticles: [],
    readMoreArticles: [],
    showDrawer: false,
  };

  componentDidMount = async () => {
    const relatedArticles = await loadArticles({
      category: this.props.category,
      exclude: this.props.article,
      limit: NUMBER_OF_RELATED_ARTICLES,
    });

    this.setState({ relatedArticles });

    const readMoreArticles = await loadReadMoreArticles(this.props.article, relatedArticles);
    this.setState({ readMoreArticles });
    window.setTimeout(() => this.setState({ showDrawer: true }), 10000);
  };

  toggleDrawer = () => {
    this.setState((prevState) => {
      eventTracking('above-fold', !prevState.showDrawer ? 'open' : 'cancel', 'icon');
      return { showDrawer: !prevState.showDrawer };
    });
  };

  render() {
    const { relatedArticles, readMoreArticles, showDrawer } = this.state;
    const { article } = this.props;
    const {
      title,
      author,
      category: { name: category, slug: categorySlug },
      tags,
      published,
      formattedPublished,
      meta,
      primaryImage,
      primaryVideo,
      primaryVideoCaption,
      content,
      readTime,
      navigationCtas,
      bottomCtas,
    } = article;
    return (
      <>
        <Container>
          <Head>
            <title>{title} - Lendi</title>
            <meta name="description" content={meta.description} />
            <meta property="og:type" content="article" />
            <meta property="og:title" content={meta.title} />
            <meta property="og:description" content={meta.description} />
            <meta property="og:image" content={`https:${primaryImage}`} />
            <meta property="og:url" content={articleUrl(article)} />
            <meta name="twitter:title" content={meta.title} />
            <meta name="twitter:description" content={meta.description} />
            <meta name="twitter:image" content={`https:${primaryImage}`} />
            <meta name="twitter:creator" content="@lendiau" />
            <link rel="canonical" href={articleUrl(article)} />
          </Head>
          <BackLink />
          <ArticleWrapper itemScope itemType="http://schema.org/Article">
            <meta itemProp="datePublished" content={published} />
            <meta itemProp="url" content={articleUrl(article)} />
            <meta itemProp="mainEntityOfPage" content={articleUrl(article)} />
            <meta itemProp="name" content={meta.title} />
            <meta itemProp="headline" content={meta.title} />
            <NextLink as={`/inspire/category/${categorySlug}`} href={`/?category=${categorySlug}`} passHref>
              <ArticleCategoryLink
                target="_blank"
                onClick={() => {
                  eventTracking('above-fold', 'navigate', category);
                }}
              >
                <ArticleCategory>{category}</ArticleCategory>
              </ArticleCategoryLink>
            </NextLink>
            <ArticleTitle>{title}</ArticleTitle>
            <ArticleInfo>
              <AuthorInfo>
                By <AuthorName itemProp="author">{author}</AuthorName>,
              </AuthorInfo>
              <time dateTime={published} itemProp="datePublished">
                {formattedPublished}
              </time>
              <ReadTimeInfo>| {readTime} min read</ReadTimeInfo>
            </ArticleInfo>
            <ArticlePrimaryImage src={primaryImage} />
            <ArticleBody>
              {
                remark()
                  .use(remark2react, {
                    remarkReactComponents: {
                      a: Link,
                      ul: Ul,
                      p: InlineCtaHOC(article.contentCtas),
                      table: Table,
                    },
                  })
                  .processSync(content).contents
              }
            </ArticleBody>
            {primaryVideo && <ArticlePrimaryVideo src={primaryVideo} caption={primaryVideoCaption} />}
            {tags.length > 0 && <ArticleTags tags={tags} />}
          </ArticleWrapper>
          {readMoreArticles.length > 0 && <ReadMoreArticles articles={readMoreArticles} />}
        </Container>
        {navigationCtas.length > 0 && <BottomCta ctas={navigationCtas} showImage={true} />}
        {relatedArticles.length > 0 && <RelatedArticles articles={relatedArticles} />}
        {bottomCtas.length > 0 && <BottomCta ctas={bottomCtas} />}
        <Drawer show={showDrawer} toggleDrawer={this.toggleDrawer} />
      </>
    );
  }
}

export async function getStaticPaths() {
  const { publicRuntimeConfig } = getConfig();
  const { CONTENTFUL_SPACE_ID, CONTENTFUL_ACCESS_TOKEN, CONTENTFUL_ENVIRONMENT } = publicRuntimeConfig;
  const client = createClient({
    space: CONTENTFUL_SPACE_ID,
    environment: CONTENTFUL_ENVIRONMENT,
    accessToken: CONTENTFUL_ACCESS_TOKEN,
  });
  const articlesEntryRes = await client.getEntries({ content_type: 'article', select: ['fields'], limit: 1000 });
  const articles = articlesEntryRes.items.map((i) => {
    return {
      slug: i.fields.slug,
      tags: (i.fields.tag as Entry[])?.map((t) => t.fields).filter((i) => i) || [],
      category: (i?.fields?.category as Entry)?.fields,
    };
  });
  const paths = articles.map((a) => {
    const category = a.category.slug || '';
    const slug = a.slug;
    return { params: { category, slug } };
  });
  return { paths, fallback: false };
}

export const getStaticProps = async (context) => {
  const [article, categories] = await Promise.all([loadArticle(context.params.slug), loadCategories()]);
  return { props: JSON.parse(JSON.stringify({ article, categories, category: article.category })) };
};

export default ArticlePage;
